import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const FinancialHealthPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
    fetchHistory();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/super-admin/financial-analytics`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/financial-history`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setHistory(response.data);
    } catch (err) {
      console.error('Failed to fetch financial history:', err);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const MetricCard = ({ title, value, icon, bgColor, textColor, subtitle }) => (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 border border-gray-200">
      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className={`p-3 rounded-lg ${bgColor}`}>
            {icon}
          </div>
          <div className={`text-3xl font-bold ${textColor}`}>
            {value}
          </div>
        </div>
        <h3 className="text-gray-700 font-semibold text-base">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading financial health analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold">Financial Health</h1>
          <p className="text-purple-200 text-sm">Churn, conversion, and customer value analytics</p>
        </div>
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => {
              fetchAnalytics();
              fetchHistory();
            }}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Financial Health</h1>
              <p className="text-purple-200 text-sm">Churn, conversion, and customer value analytics</p>
            </div>
          </div>
          <button
            onClick={() => {
              fetchAnalytics();
              fetchHistory();
            }}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-all flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Last Updated */}
      <div className="text-sm text-gray-600">
        Last updated: {formatDateTime(analytics.last_updated)}
      </div>

      {/* Financial Health Metric Cards */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          Financial Health
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <MetricCard
            title="Churn Rate"
            value={`${analytics.churn_rate}%`}
            subtitle="Cancellations this month"
            icon={<svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg>}
            bgColor="bg-orange-50"
            textColor="text-orange-800"
          />
          <MetricCard
            title="Growth Rate"
            value={`${analytics.growth_rate}%`}
            subtitle="MRR month-over-month"
            icon={<svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>}
            bgColor="bg-green-50"
            textColor="text-green-800"
          />
        </div>
      </div>

      {/* Financial Health Charts */}
      {history && history.months && (
        <div className="space-y-8">
          {/* Chart 12: Churn Rate Trend */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Churn Rate Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={history.months}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis label={{ value: 'Churn Rate (%)', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value) => `${value}%`} />
                <Legend />
                <Line type="monotone" dataKey="churn_rate" stroke="#ef4444" strokeWidth={2} name="Churn Rate" />
                {/* Benchmark line at 5-7% (industry average) */}
                <Line type="monotone" dataKey={() => 6} stroke="#94a3b8" strokeWidth={1} strokeDasharray="5 5" name="Industry Benchmark (6%)" />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> The percentage of customers who cancelled their subscriptions each month over the last 12 months, compared to a 6% industry benchmark.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> Aim to stay below 6% monthly churn (industry average for SaaS). Spikes indicate problems - check what changed that month (pricing, features, support). Consistently high churn (>10%) is critical - survey churned customers to understand why they left and address those issues immediately.
              </p>
            </div>
          </div>

          {/* Chart 13: Trial Conversion Rate Trend */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Trial Conversion Rate Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={history.months}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis label={{ value: 'Conversion Rate (%)', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value) => `${value}%`} />
                <Legend />
                <Line type="monotone" dataKey="conversion_rate" stroke="#10b981" strokeWidth={2} name="Conversion Rate" />
                {/* Target line at 25% (good conversion rate) */}
                <Line type="monotone" dataKey={() => 25} stroke="#94a3b8" strokeWidth={1} strokeDasharray="5 5" name="Target (25%)" />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> The percentage of free trials that converted to paid subscriptions each month, compared to a 25% target conversion rate.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> Above 25% is excellent. 15-25% is good. Below 15% needs attention. Low conversion means trials aren't experiencing enough value - improve onboarding, highlight key features earlier, or add in-trial support. Track what high-converting cohorts did differently and replicate that experience.
              </p>
            </div>
          </div>

          {/* Chart 14: Customer Lifetime Value Distribution */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Lifetime Value (LTV) Distribution</h3>
            {analytics && analytics.ltv_distribution ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                  data={Object.entries(analytics.ltv_distribution).map(([range, count]) => ({
                    range,
                    count
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#6366f1" name="Customers" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                Loading LTV data...
              </div>
            )}
            <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> A histogram showing how many customers fall into different lifetime value ranges (e.g., £0-100, £101-500, £501-1000, etc.).
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> Identifies your high-value vs low-value customers. If most customers cluster at low LTV, focus on retention and upselling. A few high-LTV customers? Create VIP programs and account management for them. Long tail of low-LTV? Consider raising minimum viable product price or improving onboarding to increase early retention.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialHealthPage;
